"use client"

import { useState } from "react"
import { ChevronDown } from "lucide-react"

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"

interface Props {
  values: string[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
}

export function ColumnFilter({ values, selected, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [localChecked, setLocalChecked] = useState<Set<string>>(new Set(values))

  const isActive = selected.size > 0

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setLocalChecked(selected.size === 0 ? new Set(values) : new Set(selected))
    }
    setOpen(next)
  }

  const toggle = (v: string) => {
    const next = new Set(localChecked)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    setLocalChecked(next)
    if (next.size === 0 || next.size === values.length) {
      onChange(new Set())
    } else {
      onChange(next)
    }
  }

  const selectAll = () => {
    setLocalChecked(new Set(values))
    onChange(new Set())
  }

  const deselectAll = () => {
    setLocalChecked(new Set())
    onChange(new Set())
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex items-center rounded p-0.5 transition-colors hover:text-zinc-100",
            isActive ? "text-emerald-400" : "text-zinc-500"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <ChevronDown className={cn("size-3 transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2">
        <div className="mb-2 flex gap-1 border-b border-zinc-700 pb-2">
          <button
            onClick={selectAll}
            className="flex-1 rounded px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100"
          >
            전체선택
          </button>
          <button
            onClick={deselectAll}
            className="flex-1 rounded px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100"
          >
            전체해제
          </button>
        </div>
        <div className="max-h-52 space-y-0.5 overflow-y-auto">
          {values.map((v) => (
            <label
              key={v}
              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-zinc-800"
            >
              <Checkbox
                checked={localChecked.has(v)}
                onCheckedChange={() => toggle(v)}
              />
              <span className="text-xs text-zinc-300">{v}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
